import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { BarChart3, TrendingUp, Settings, Trash2, Plus, GripVertical, ChevronUp } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import RankingSidebar from './RankingSidebar';
import ProductSelectionModal from './ProductSelectionModal';
import { useRanking } from '../../hooks/useRanking';
import { useToast } from '../../hooks/use-toast';

const RankingManagement: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [selectedRankingType, setSelectedRankingType] = useState<'new-arrivals' | 'top-selling'>('new-arrivals');
  const [isReordering, setIsReordering] = useState(false);
  const [reorderingTable, setReorderingTable] = useState<string | null>(null);
  const { newArrivals, topSelling, loading, addProductToRanking, reorderRankings, removeProductFromRanking } = useRanking();
  const { toast } = useToast();

  const handleOpenSidebar = () => {
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleOpenProductModal = (rankingType: 'new-arrivals' | 'top-selling') => {
    setSelectedRankingType(rankingType);
    setIsProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setIsProductModalOpen(false);
  };

  const handleSelectProducts = async (productIds: string[]) => {
    const table = selectedRankingType === 'new-arrivals' ? 'newariverank' : 'topsellrank';
    
    for (const productId of productIds) {
      try {
        await addProductToRanking(table, productId);
      } catch (error) {
        console.error('Failed to add product:', error);
        throw error;
      }
    }

    // Auto-scroll to bottom after adding products
    setTimeout(() => {
      const containerId = selectedRankingType === 'new-arrivals' ? 'new-arrivals-preview' : 'top-selling-preview';
      const container = document.querySelector(`[data-rbd-droppable-id="${containerId}"]`);
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  };

  const handleDeleteProduct = async (productId: string, table: 'newariverank' | 'topsellrank') => {
    try {
      await removeProductFromRanking(table, productId);
      toast({
        title: "Product Removed",
        description: "Product has been removed from the ranking.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove product from ranking.",
        variant: "destructive",
      });
    }
  };

  const scrollToTop = (containerId: string) => {
    const container = document.querySelector(`[data-rbd-droppable-id="${containerId}"]`);
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDragEnd = async (result: any, table: 'newariverank' | 'topsellrank') => {
    if (!result.destination) return;

    const items = table === 'newariverank' ? newArrivals : topSelling;
    const reorderedItems = Array.from(items);
    const [reorderedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, reorderedItem);

    // Set loading state
    setIsReordering(true);
    setReorderingTable(table);

    try {
      console.log('Reordering items:', reorderedItems.map(item => ({ id: item.id, rank: item.rank, name: item.product_name })));
      await reorderRankings(table, reorderedItems);
      console.log('Reordering completed successfully');
      
      toast({
        title: "Ranking Updated",
        description: `Product rankings have been successfully reordered.`,
      });
    } catch (error) {
      console.error('Failed to reorder rankings:', error);
      toast({
        title: "Error",
        description: "Failed to save ranking changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      // Clear loading state
      setIsReordering(false);
      setReorderingTable(null);
    }
  };

  const renderDraggableProductCard = (item: any, index: number, table: 'newariverank' | 'topsellrank') => {
    const isCurrentlyReordering = isReordering && reorderingTable === table;
    
    return (
      <Draggable key={item.id} draggableId={item.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
              snapshot.isDragging 
                ? 'shadow-lg scale-105 bg-blue-50 border-2 border-blue-200' 
                : isCurrentlyReordering
                ? 'bg-yellow-50 border border-yellow-200 opacity-75'
                : 'bg-gray-50 hover:bg-gray-100 hover:shadow-sm'
            }`}
          >
            <div
              {...provided.dragHandleProps}
              className={`cursor-grab active:cursor-grabbing flex-shrink-0 transition-colors ${
                isCurrentlyReordering ? 'text-yellow-500' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <GripVertical size={14} />
            </div>
            
            <Badge 
              variant="outline" 
              className={`text-xs px-2 py-1 flex-shrink-0 transition-colors ${
                isCurrentlyReordering ? 'border-yellow-300 text-yellow-700' : ''
              }`}
            >
              #{item.rank}
            </Badge>

            {/* Product Image */}
            <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-100">
              {item.product_image ? (
                <img 
                  src={item.product_image} 
                  alt={item.product_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <span className="text-xs text-gray-500">No Image</span>
                </div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate transition-colors ${
                isCurrentlyReordering ? 'text-yellow-800' : ''
              }`}>
                {item.product_name}
              </p>
              <p className={`text-sm transition-colors ${
                isCurrentlyReordering ? 'text-yellow-600' : 'text-gray-500'
              }`}>
                ₹{item.product_price?.toLocaleString()}
              </p>
            </div>
            
            <div className="flex items-center space-x-1 text-gray-400 flex-shrink-0">
              {isCurrentlyReordering ? (
                <div className="flex items-center space-x-1">
                  <div className="animate-spin rounded-full h-2.5 w-2.5 border-b-2 border-yellow-500"></div>
                  <span className="text-xs text-yellow-600">...</span>
                </div>
              ) : (
                <button
                  onClick={() => handleDeleteProduct(item.product_id, table)}
                  className="p-1 hover:bg-red-100 hover:text-red-600 rounded transition-colors"
                  title="Delete product from ranking"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ranking Management</h1>
          <p className="text-gray-600">Manage product rankings for New Arrivals and Top Selling collections</p>
        </div>
        <Button onClick={handleOpenSidebar} className="flex items-center space-x-2">
          <Settings size={16} />
          <span>Manage Rankings</span>
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="flex gap-4">
        <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-full">
          <BarChart3 className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">New Arrivals</span>
          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-800">
            {newArrivals.length}/10
          </Badge>
        </div>

        <div className="flex items-center space-x-2 bg-green-50 px-4 py-2 rounded-full">
          <TrendingUp className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-900">Top Selling</span>
          <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
            {topSelling.length}/10
          </Badge>
        </div>
      </div>

      {/* Quick Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* New Arrivals Preview */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2 text-sm">
                <BarChart3 size={16} />
                <span>New Arrivals Preview</span>
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenProductModal('new-arrivals')}
                disabled={newArrivals.length >= 10}
                className="flex items-center space-x-1 text-xs px-2 py-1 h-7"
              >
                <Plus size={12} />
                <span>Add</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : newArrivals.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <BarChart3 size={24} className="mx-auto mb-1 text-gray-300" />
                <p className="text-xs">No products in New Arrivals</p>
                <p className="text-xs">Click "Add" to get started</p>
              </div>
            ) : (
              <div className="relative">
                <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'newariverank')}>
                  <Droppable droppableId="new-arrivals-preview">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-2 transition-all duration-200 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 scroll-smooth ${
                          isReordering && reorderingTable === 'newariverank' ? 'opacity-60' : 'opacity-100'
                        }`}
                      >
                        {newArrivals.slice(0, 10).map((item, index) => 
                          renderDraggableProductCard(item, index, 'newariverank')
                        )}
                        {provided.placeholder}
                        {newArrivals.length > 10 && (
                          <p className="text-xs text-gray-500 text-center">
                            +{newArrivals.length - 10} more products
                          </p>
                        )}
                      </div>
                    )}
                  </Droppable>
                  {/* Scroll indicator gradient */}
                  {newArrivals.length > 5 && (
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  )}
                  
                  {/* Scroll to top button */}
                  {newArrivals.length > 5 && (
                    <button
                      onClick={() => scrollToTop('new-arrivals-preview')}
                      className="absolute top-2 right-2 p-1 bg-white hover:bg-gray-50 rounded-full shadow-md transition-colors"
                      title="Scroll to top"
                    >
                      <ChevronUp size={14} className="text-gray-600" />
                    </button>
                  )}
                </DragDropContext>
                
                {/* Loading overlay */}
                {isReordering && reorderingTable === 'newariverank' && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                    <div className="flex items-center space-x-2 text-blue-600">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="text-sm font-medium">Updating rankings...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Selling Preview */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2 text-sm">
                <TrendingUp size={16} />
                <span>Top Selling Preview</span>
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenProductModal('top-selling')}
                disabled={topSelling.length >= 10}
                className="flex items-center space-x-1 text-xs px-2 py-1 h-7"
              >
                <Plus size={12} />
                <span>Add</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-4">Loading...</div>
            ) : topSelling.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <TrendingUp size={24} className="mx-auto mb-1 text-gray-300" />
                <p className="text-xs">No products in Top Selling</p>
                <p className="text-xs">Click "Add" to get started</p>
              </div>
            ) : (
              <div className="relative">
                <DragDropContext onDragEnd={(result) => handleDragEnd(result, 'topsellrank')}>
                  <Droppable droppableId="top-selling-preview">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-2 transition-all duration-200 max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 scroll-smooth ${
                          isReordering && reorderingTable === 'topsellrank' ? 'opacity-60' : 'opacity-100'
                        }`}
                      >
                        {topSelling.slice(0, 10).map((item, index) => 
                          renderDraggableProductCard(item, index, 'topsellrank')
                        )}
                        {provided.placeholder}
                        {topSelling.length > 10 && (
                          <p className="text-xs text-gray-500 text-center">
                            +{topSelling.length - 10} more products
                          </p>
                        )}
                      </div>
                    )}
                  </Droppable>
                  {/* Scroll indicator gradient */}
                  {topSelling.length > 5 && (
                    <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  )}
                  
                  {/* Scroll to top button */}
                  {topSelling.length > 5 && (
                    <button
                      onClick={() => scrollToTop('top-selling-preview')}
                      className="absolute top-2 right-2 p-1 bg-white hover:bg-gray-50 rounded-full shadow-md transition-colors"
                      title="Scroll to top"
                    >
                      <ChevronUp size={14} className="text-gray-600" />
                    </button>
                  )}
                </DragDropContext>
                
                {/* Loading overlay */}
                {isReordering && reorderingTable === 'topsellrank' && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                    <div className="flex items-center space-x-2 text-blue-600">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="text-sm font-medium">Updating rankings...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>How to Manage Rankings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">1</div>
              <p>Use "Add Product" buttons in each preview section to add products to rankings</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">2</div>
              <p><strong>Drag and drop products directly in the preview cards</strong> to reorder rankings</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">3</div>
              <p>Click "Manage Rankings" to open the detailed ranking management sidebar</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">4</div>
              <p>Switch between "New Arrivals" and "Top Selling" tabs (max 10 products each)</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">5</div>
              <p>Remove products using the trash icon to make room for new ones</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">6</div>
              <p>Changes are automatically saved to the database</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ranking Sidebar */}
      <RankingSidebar isOpen={isSidebarOpen} onClose={handleCloseSidebar} />

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={isProductModalOpen}
        onClose={handleCloseProductModal}
        onSelectProducts={handleSelectProducts}
        rankingType={selectedRankingType}
        currentCount={selectedRankingType === 'new-arrivals' ? newArrivals.length : topSelling.length}
        maxCount={10}
      />
    </div>
  );
};

export default RankingManagement;
